import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Divider,
  Grid,
  useMediaQuery,
  useTheme,
  Paper,
} from "@mui/material";
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";
import { useAppSelector, useAppDispatch } from "../../hooks/hooks";
import { updateUserDetails } from "../../redux/userSlice";
import { UserProfile } from "../../models/UserProfile";

// Get width from environment variable with fallback
const MOBILE_APP_WIDTH = parseInt(
  process.env.REACT_APP_MOBILE_APP_WIDTH || "500"
);

interface UserDetailsSectionProps {
  isMobile?: boolean;
}

const UserDetailsSection: React.FC<UserDetailsSectionProps> = ({
  isMobile = false,
}) => {
  const theme = useTheme();
  const [isEditing, setIsEditing] = useState(false);
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.user);
  const [editedUser, setEditedUser] = useState<UserProfile>(
    user ?? {
      userName: "",
      emailAddress: "",
      name: "",
      surname: "",
      fullName: "",
      walletAddress: "",
      securityTokenA: "",
      verificationCodeB: "",
      accessPhraseC: "",
      identityMarkerD: "",
      onBoardingCompleted: false,
      phoneNumber: "",
    }
  );

  const handleEdit = () => setIsEditing(true);
  const handleCancel = () => setIsEditing(false);

  const handleSave = async () => {
    await dispatch(updateUserDetails(editedUser));
    setIsEditing(false);
  };

  const handleChange = (field: keyof UserProfile, value: string) => {
    setEditedUser((prev) => ({
      ...prev,
      [field]: value,
      fullName:
        field === "name" || field === "surname"
          ? `${field === "name" ? value : prev.name} ${
              field === "surname" ? value : prev.surname
            }`
          : prev.fullName,
    }));
  };

  return (
    <Paper
      sx={{
        p: isMobile ? 2 : 3,
        width: "100%",
        maxWidth: MOBILE_APP_WIDTH,
        margin: "auto",
        ...(isMobile && {
          maxWidth: "100%",
          borderRadius: 0,
          boxShadow: "none",
        }),
        ...(!isMobile && {
          borderRadius: 2,
          boxShadow: 3,
        }),
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography
          variant="h6"
          gutterBottom
          sx={{ fontSize: isMobile ? "1.25rem" : "1.5rem" }}
        >
          User Details
        </Typography>
        {!isEditing ? (
          <Button
            variant="outlined"
            startIcon={<EditIcon fontSize={isMobile ? "small" : "medium"} />}
            onClick={handleEdit}
            size={isMobile ? "small" : "medium"}
          >
            Edit
          </Button>
        ) : (
          <Box>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon fontSize={isMobile ? "small" : "medium"} />}
              onClick={handleSave}
              sx={{ mr: 1 }}
              size={isMobile ? "small" : "medium"}
            >
              Save
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={
                <CancelIcon fontSize={isMobile ? "small" : "medium"} />
              }
              onClick={handleCancel}
              size={isMobile ? "small" : "medium"}
            >
              Cancel
            </Button>
          </Box>
        )}
      </Box>
      <Divider sx={{ my: 2 }} />

      <Grid container spacing={isMobile ? 1 : 3}>
        <Grid>
          <TextField
            label="Username"
            value={isEditing ? editedUser.userName : user?.userName}
            onChange={(e) => handleChange("userName", e.target.value)}
            fullWidth
            margin="normal"
            disabled={!isEditing}
            size={isMobile ? "small" : "medium"}
            InputProps={{
              readOnly: !isEditing,
            }}
          />
        </Grid>
        <Grid>
          <TextField
            label="Email"
            value={isEditing ? editedUser.emailAddress : user?.emailAddress}
            onChange={(e) => handleChange("emailAddress", e.target.value)}
            fullWidth
            margin="normal"
            disabled={!isEditing}
            size={isMobile ? "small" : "medium"}
            InputProps={{
              readOnly: !isEditing,
            }}
          />
        </Grid>
        <Grid>
          <TextField
            label="First Name"
            value={isEditing ? editedUser.name : user?.name}
            onChange={(e) => handleChange("name", e.target.value)}
            fullWidth
            margin="normal"
            disabled={!isEditing}
            size={isMobile ? "small" : "medium"}
            InputProps={{
              readOnly: !isEditing,
            }}
          />
        </Grid>
        <Grid>
          <TextField
            label="Last Name"
            value={isEditing ? editedUser.surname : user?.surname}
            onChange={(e) => handleChange("surname", e.target.value)}
            fullWidth
            margin="normal"
            disabled={!isEditing}
            size={isMobile ? "small" : "medium"}
            InputProps={{
              readOnly: !isEditing,
            }}
          />
        </Grid>
        <Grid>
          <TextField
            label="Full Name"
            value={isEditing ? editedUser.fullName : user?.fullName}
            fullWidth
            margin="normal"
            disabled
            size={isMobile ? "small" : "medium"}
            InputProps={{
              readOnly: true,
            }}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

export default UserDetailsSection;
